import { DrawerDialog } from '@/common/components/drawer-dialog';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { VehiclePlan } from '../types';
import AccordionList from '@/common/components/accordion-list';
import { AccordionListItem } from '@/common/types';
import SubtractThick from '@/icons/subtract-thick';
import AddThick from '@/icons/add-thick';
import PDPPlanInfoModalTrigger from './pdp-plan-info-modal-trigger';
import { getDynamicCurrency } from '@/util-conversion/currency/currency-to-string';

interface PDPPlanSelectionHelperDrawerDialogProps {
  vehiclePlans: VehiclePlan[];
  className?: string;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
}

export default function PDPPlanSelectionHelperDrawerDialog({
  vehiclePlans,
  className,
  price_savings_1200_over_850,
  price_difference_850_vs_1200,
}: PDPPlanSelectionHelperDrawerDialogProps) {
  const lowGearPlan = vehiclePlans.find((plan) => plan.miles_per_month === 0);
  const starterPlan = vehiclePlans.find((plan) => plan.miles_per_month === 850);
  const cruiserPlan = vehiclePlans.find((plan) => plan.miles_per_month === 1200);
  const planSelectionHelper: AccordionListItem[] = [
    {
      title: <p className="body-strong">What&apos;s the right plan for me?</p>,
      content: [
        {
          body: 'Not sure where to start? Our plans are designed to fit your lifestyle:',
        },
      ],
      list: [
        'The Standard Plan is perfect for anyone who drives under 10,000 miles per year, making it great for short commutes and local trips.',
        'The Cruiser Plan is perfect for anyone who drives over 10,000 miles per year, ideal for average commutes, road trips, and everyday use.',
      ],
      footer: cruiserPlan && starterPlan && price_savings_1200_over_850 && (
        <p className="body font-sans">
          The Cruiser Plan is our best value because you save{' '}
          {getDynamicCurrency(price_savings_1200_over_850)} per month if you drive 1,200
          miles. On top of that, you save 40% on additional miles over 1,200 per month (
          {getDynamicCurrency(cruiserPlan?.replenishment_price_per_mile)}/mi vs.
          {getDynamicCurrency(starterPlan?.replenishment_price_per_mile)}/mi).
        </p>
      ),
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
    {
      title: <p className="body-strong">What happens if I run out of miles?</p>,
      content: [
        {
          body: "Running out of miles? Don't worry—Flexcar makes it simple",
        },
      ],
      list: [
        starterPlan &&
          starterPlan.total_replenishment_amount &&
          `Standard Plan: If you run out of miles, ${starterPlan.default_replenishment_miles_amount} additional miles will be added for ${getDynamicCurrency(starterPlan.total_replenishment_amount)} (${getDynamicCurrency(starterPlan.replenishment_price_per_mile)}/mi).`,
        cruiserPlan &&
          cruiserPlan.total_replenishment_amount &&
          `Cruiser Plan: If you run out of miles, ${cruiserPlan.default_replenishment_miles_amount} additional miles will be added for ${getDynamicCurrency(cruiserPlan.total_replenishment_amount)} (${getDynamicCurrency(cruiserPlan.replenishment_price_per_mile)}/mi), ~40% savings compared to Standard Plan.`,
      ].filter((item): item is string => Boolean(item)),
      footer: (
        <p className="body font-sans">
          No surprises—just fair, predictable pricing for additional miles.
        </p>
      ),
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
    {
      title: <p className="body-strong">How do I change my plan?</p>,
      content: [
        {
          body: 'Changing your plan is simple and flexible—just like everything at Flexcar',
        },
      ],
      list: [
        starterPlan &&
          cruiserPlan &&
          starterPlan?.total_price_per_month &&
          cruiserPlan?.total_price_per_month &&
          price_difference_850_vs_1200 &&
          `Upgrade: Need more miles? You can switch to the Cruiser Plan anytime, and you'll only pay the difference for the remaining month. For example, if you're on the Standard Plan (${getDynamicCurrency(starterPlan?.total_price_per_month)}/month) and upgrade to the Cruiser Plan (${getDynamicCurrency(cruiserPlan?.total_price_per_month)}/month), you'll pay the ${getDynamicCurrency(price_difference_850_vs_1200)} difference and get the extra 350 miles right away.`,
        `Downgrade: Want to scale back? You can switch to the Standard Plan or the Low Gear Plan, and the change will take effect at your next billing cycle. If you move to the Low Gear Plan, your current mileage balance will remain, but you won't receive monthly replenishments.`,
      ].filter((item): item is string => Boolean(item)),
      footer: (
        <p className="body font-sans">
          All changes can be made in seconds right from the Flexcar app.
        </p>
      ),
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
    {
      title: <p className="body-strong">Do miles roll over?</p>,
      content: [
        {
          body: 'Yes! Any unused miles roll over to the next month, even if you swap cars or change plans. With the Low Gear Plan, you can use up your mileage balance without getting monthly replenishments.',
        },
      ],
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
    {
      title: <p className="body-strong">How does mileage replenishment work?</p>,
      content: [
        {
          body: `Every month, your selected mileage plan adds to your balance automatically. If you don't use all your miles, they roll over. And if you need more mid-month, additional miles are charged as outlined in the "What happens if I run out of miles?" section.`,
        },
      ],
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
    {
      title: (
        <p className="body-strong">
          What if I&apos;ve accumulated miles or don&apos;t need additional monthly miles?
        </p>
      ),
      content: [
        {
          body: "The Low Gear Plan is perfect if you're out of town or don't need to drive for a while. It lets you stop accumulating monthly miles while keeping your car or use up a large mileage balance from a larger plan without monthly replenishments.",
        },
        {
          body: 'Important Note:',
        },
      ],
      list: [
        `The Low Gear Plan is only available after you've picked up your car. You'll need to start with either the Standard Plan or the Cruiser Plan when placing your order to ensure you begin with a mileage balance.`,
        `Switch to the Low Gear Plan anytime in the Flexcar app.`,
      ],
      footer: (
        <>
          {lowGearPlan &&
          lowGearPlan.total_replenishment_amount &&
          lowGearPlan.replenishment_price_per_mile ? (
            <p className="body mt-1">
              Low Gear Plan Pricing: If you run out of miles, you&apos;ll be charged{' '}
              {lowGearPlan.default_replenishment_miles_amount ?? 0} miles for{' '}
              {getDynamicCurrency(lowGearPlan.total_replenishment_amount) ?? '$0.00'} (
              {getDynamicCurrency(lowGearPlan.replenishment_price_per_mile) ?? '$0.00'}
              /mi) runs out.
            </p>
          ) : null}
        </>
      ),
      iconOverride: {
        openedIcon: <SubtractThick className="h-[20px] w-[18px] shrink-0 self-start" />,
        closedIcon: <AddThick className="h-[20px] w-[18px] shrink-0 self-start" />,
      },
    },
  ];

  function onOpenChange(open: boolean) {
    if (open) fullstoryEvent('your_monthly_plan');
  }

  return (
    <DrawerDialog
      trigger={
        <PDPPlanInfoModalTrigger
          className={className}
          helpSmallClassName="h-[24px] w-[24px]"
        />
      }
      onOpenChange={onOpenChange}
      title="Monthly Plan Details"
    >
      <AccordionList
        accordionItems={planSelectionHelper}
        variant={'default'}
        separateItemsWithHorizontalRule={true}
        defaultOpenItem={0}
      />
    </DrawerDialog>
  );
}
